import { useState } from "react";
import FlyerPreview from "./FlyerPreview";
import { useTranslation } from "react-i18next";
import BoycottHeaderEditor from "./editor/BoycottHeaderEditor";
import EuropeHeaderEditor from "./editor/EuropeHeaderEditor";
import AlternativesEditor, { AlternativesVP } from "./editor/AlternativesEditor";
import { Spacer } from "./editor/EditorHelpers";
import { emojiFromLocale } from "./locales/locales";
import FooterEditor from "./editor/FooterEditor";
import styled from "styled-components";
import useLayout from "./util/useLayout";
import { FlyerType } from "./FlyerTypePicker";
import BackgroundPicker, { BackgroundProps } from "./components/BackgroundPicker";
import GreenHeaderEditor, { GreenHeaderProps } from "./editor/GreenHeaderEditor";
import { useFlyerConfig } from "./util/useFlyerConfig";
import EditorPreviewContainer from "./EditorPreviewContainer";
import FlyerContentChildCanvasAndTable from "./preview/FlyerContentChildCanvasAndTable";
import FlyerContentChildGreen from "./preview/FlyerContentChildGreen";
import { useRestorable, v } from "./util/useRestorable";
import { PresetTextEditor } from "./components/TextEditor";
import { PresetColorEditor } from "./components/ColorPicker";
import { useIsHardNo } from "./util/useDomain";

/*
  Rich people in America hold a lot of power. If they find out Trump and Musk are bad for business, they will try to reign them in.
*/

/*
  Every time you decide against a product from the US it helps 🫶🇪🇺🇨🇦🇺🇦
*/

const textColorDefaults = {
  [FlyerType.BOYCOTT]: "#3f3424",
  [FlyerType.EUROPE]: "#003399",
  [FlyerType.GREEN]: "#0F3324",
  [FlyerType.BROCHURE]: "#000000",
}

const getDefaultSubheader = (t, flyerType: FlyerType, flyerLocale) => {
  // using if-else to get help from i18n-ally on missing translations, 
  // while also preventing extra calls to t
  const subheader = flyerType === FlyerType.BOYCOTT
    ? t('default.boycott.subheader')
    : flyerType === FlyerType.EUROPE
    ? t('default.europe.subheader')
    : flyerType === FlyerType.GREEN
    ? t('default.green.subheader')
    : "";
  const heartHandsIndex = subheader.indexOf('🫶');
  if (heartHandsIndex >= 0) {
    const insertPosition = heartHandsIndex + '🫶'.length;
    const before = subheader.slice(0, insertPosition);
    const after = subheader.slice(insertPosition);
    return (before + emojiFromLocale(flyerLocale) + after);
  }
  return subheader;
}

const getDefaultBackgroundImage = (flyerType: FlyerType, isHardNo: boolean) => {
  return flyerType === FlyerType.GREEN
    ? "/green_background.jpg"
    : flyerType === FlyerType.BROCHURE
    ? (isHardNo ? "/hardno_brochure_front_page_no_text.png" : "/brochure_front_page_no_text.png")
    : null;
}

export default function FlyerEditor() {
  const { flyerType, flyerLocale } = useFlyerConfig();
  const { t } = useTranslation();
  const { t: dt } = useTranslation(null, { lng: flyerLocale.substring(0, 2) })
  const { isHardNo } = useIsHardNo();
  const [subheader, setSubheader] = useRestorable(getDefaultSubheader(dt, flyerType, flyerLocale), 'subheader')
  const [textColor, setTextColor] = useRestorable(textColorDefaults[flyerType], 'textColor');
  const [background, setBackground] = useState<BackgroundProps>({ backgroundColor: "#ffffff" });
  const [alternatives, setAlternatives] = useState<null | AlternativesVP>(null);
  const { isSmallScreen } = useLayout();
  
  const [headerProps, setHeaderProps] = useState(null);
  const [footerProps, setFooterProps] = useState(null);

  const hasSubHeader = [ 
    FlyerType.EUROPE, 
    FlyerType.BOYCOTT,
    FlyerType.GREEN
  ].includes(flyerType)
  const hasAlternativesTable = [ 
    FlyerType.EUROPE, 
    FlyerType.BOYCOTT,
    FlyerType.GREEN,
  ].includes(flyerType)
  const canvasAndTableTypes = [
    FlyerType.BOYCOTT,
    FlyerType.EUROPE,
  ];

  const getAnalyticsDetails = () => {
    return JSON.stringify({
      headerProps,
      subheader,
      alternatives,
      footerProps,
      background,
      textColor
    })
  };

  return (
    <EditorPreviewContainer>
      {/* Left Side - Form */}
      {<FlyerForm $isSmallScreen={isSmallScreen}>
        <h2>{t('flyer-editor.title')} {emojiFromLocale(flyerLocale)}</h2>
        {(flyerType == FlyerType.BOYCOTT) && <BoycottHeaderEditor 
          onChange={(props) => setHeaderProps(props)}
        />}
        {(flyerType == "europe") && <EuropeHeaderEditor 
          onChange={(props) => setHeaderProps(props)}
          flyerType={flyerType}
          flyerLocale={flyerLocale}
        />}
        {(flyerType == "green") && <GreenHeaderEditor 
          onChange={(props) => setHeaderProps(props)}
          flyerType={flyerType}
          flyerLocale={flyerLocale}
        />}
        {hasSubHeader && <>
          <Spacer $height="15px" />
          <PresetTextEditor 
            label={t('subheader.label')}
            value={subheader}
            onChange={setSubheader}
          />
        </>}
        <Spacer $height="15px" />
        <PresetColorEditor 
          label={t('text-color.label')}
          value={textColor}
          onChange={setTextColor} 
        />
        <Spacer $height="15px" />
        <BackgroundPicker 
          label={t('background.label')}
          defaultColor={background.backgroundColor}
          defaultImage={getDefaultBackgroundImage(flyerType, isHardNo)}
          onChange={setBackground} 
        />
        {hasAlternativesTable && <>
          <Spacer $height="15px" />
          <AlternativesEditor
            onChange={(update)  => setAlternatives(update)}
          />
        </>}
        <Spacer $height="15px" />
        <FooterEditor
          onChange={(props) => setFooterProps(props)}
        />
      </FlyerForm>}
      
      {/* Right Side - Flyer Preview */}
      {<FlyerPreview 
        background={background}
        getAnalyticsDetails={getAnalyticsDetails}
        >
        {canvasAndTableTypes.includes(flyerType)
          ? (<FlyerContentChildCanvasAndTable 
              headerProps={headerProps}
              subheader={v(subheader)}
              footerProps={footerProps}
              textColor={v(textColor)}
              alternatives={alternatives}
          />) : flyerType === FlyerType.GREEN
        ? (<FlyerContentChildGreen
          headerProps={headerProps as GreenHeaderProps}
          subheader={v(subheader)}
          textColor={v(textColor)}
          footerProps={footerProps}
          alternatives={alternatives}
        />) : (<div>Unsupported flyer type: {flyerType}</div>)}        
      </FlyerPreview>}
    </EditorPreviewContainer>
  );
}

interface SmallScreenable {
  $isSmallScreen: boolean;
}

export const FlyerForm = styled.div<SmallScreenable>`
  flex-grow: 1;
  width: ${(props) => props.$isSmallScreen ? '100%' : 'auto'};
  height: ${(props) => props.$isSmallScreen ? 'auto' : 'calc(100vh - 55px)'};
  min-width: 375px;
  overflow-y: ${(props) => props.$isSmallScreen ? 'hidden' : 'auto'};
  padding: ${(props) => props.$isSmallScreen ? '10px' : '20px'};
  background: var(--bg-color);
`;

/*
.flyer-form {
  height: ;
  min-width: 375px;
  overflow-y: auto;
  padding: 20px;
  background: var(--bg-color);
}
  .flyer-form {
    width: 100%;
    height: auto; 
    overflow-y: hidden;
    border-right: none;
    border-bottom: 2px solid #ddd;
  }

*/